import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  guestAccessSummary,
  guestMergeOnSignInNote,
} from './guestAccessCopy';
import { useAuth } from './AuthContext';
import { hasLoggedInBefore } from './priorLoginCookie';
import GoogleLoginButton from './GoogleLoginButton';
import {
  Alert,
  Box,
  Button,
  Divider,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

type AuthFormProps = {
  redirectedFrom?: string | null;
  onAuthenticated: () => void;
  compact?: boolean;
};

const AuthForm = ({
  redirectedFrom,
  onAuthenticated,
  compact = false,
}: AuthFormProps) => {
  const {
    loginWithGoogle,
    loginWithPassword,
    registerWithPassword,
    continueAsGuest,
    isLoggedIn,
  } = useAuth();
  const [mode, setMode] = useState<'signin' | 'register'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const showGuestOption = hasLoggedInBefore() && !isLoggedIn;

  const handlePasswordAuth = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setInfoMessage('');
    setSubmitting(true);

    try {
      if (mode === 'register') {
        const message = await registerWithPassword(email, password);
        setInfoMessage(
          message ??
            'Check your inbox and junk folder for a 6-digit code to confirm your account.',
        );
      } else {
        await loginWithPassword(email, password);
        onAuthenticated();
      }
    } catch (authError) {
      const message =
        authError instanceof Error
          ? authError.message
          : 'Unable to sign in. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const switchMode = () => {
    setMode(mode === 'signin' ? 'register' : 'signin');
    setError('');
    setInfoMessage('');
  };

  const handleContinueAsGuest = async () => {
    setError('');
    setInfoMessage('');
    setSubmitting(true);

    try {
      await continueAsGuest();
      onAuthenticated();
    } catch (guestError) {
      const message =
        guestError instanceof Error
          ? guestError.message
          : 'Unable to start a guest session. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: compact ? 420 : 440,
        p: { xs: 3, sm: 4 },
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Stack spacing={3}>
        {redirectedFrom && (
          <Alert severity="info">
            Sign in to continue where you left off.
          </Alert>
        )}

        {!showGuestOption && (
          <Alert severity="info">
            {guestAccessSummary} {guestMergeOnSignInNote}
          </Alert>
        )}

        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant={compact ? 'h4' : 'h5'}
            sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}
          >
            {mode === 'signin' ? 'Sign in' : 'Create account'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {mode === 'signin'
              ? 'Import and analyze your games, see refutations when you miss, and track progress.'
              : 'Start your free trial — no credit card required.'}
          </Typography>
        </Box>

        <GoogleLoginButton onClick={loginWithGoogle} />

        <Divider>
          <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
            or
          </Typography>
        </Divider>

        <Box
          component="form"
          onSubmit={(event) => void handlePasswordAuth(event)}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          {error && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          )}

          {infoMessage && (
            <Alert severity="success" sx={{ width: '100%' }}>
              {infoMessage}
            </Alert>
          )}

          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
            required
            fullWidth
            helperText={
              mode === 'register'
                ? 'Use at least 8 characters with letters and numbers.'
                : undefined
            }
          />

          {mode === 'signin' && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1 }}>
              <Link component={RouterLink} to="/forgot-password" variant="body2">
                Forgot password?
              </Link>
            </Box>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={submitting}
            sx={{ py: 1.25, textTransform: 'none', fontWeight: 600 }}
          >
            {submitting
              ? 'Please wait…'
              : mode === 'signin'
                ? 'Sign in with email'
                : 'Create account'}
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          {mode === 'signin' ? "Don't have an account?" : 'Already have an account?'}{' '}
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={switchMode}
            sx={{ fontWeight: 600, verticalAlign: 'baseline' }}
          >
            {mode === 'signin' ? 'Create one' : 'Sign in'}
          </Link>
        </Typography>

        {showGuestOption && (
          <>
            <Divider>
              <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                or
              </Typography>
            </Divider>
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              size="large"
              disabled={submitting}
              onClick={() => void handleContinueAsGuest()}
              sx={{ py: 1.25, textTransform: 'none', fontWeight: 600 }}
            >
              Continue as guest
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              {guestAccessSummary}
            </Typography>
          </>
        )}
      </Stack>
    </Paper>
  );
};

export default AuthForm;
